import * as XLSX from "xlsx";

// Parse a CSV / XLSX file into { columns, rows } where rows are objects keyed by header.
export function parseSpreadsheet(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
        const header = XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || [];
        const columns = rows.length ? Object.keys(rows[0]) : header.map(String);
        resolve({ columns, rows });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Normalized field definitions per document type.
const F = {
  vessel: { key: "vessel", label: "Vessel" },
  lane: { key: "lane", label: "Lane / Route" },
  origin: { key: "origin", label: "Origin (POL)" },
  destination: { key: "destination", label: "Destination (POD)" },
  etd: { key: "etd", label: "ETD (sailing date)" },
  week: { key: "week", label: "Week (optional — else derived from ETD)" },
  reference: { key: "reference", label: "Reference / Booking" },
  description: { key: "description", label: "Description / Charge" },
  unit: { key: "unit", label: "Unit (UOM)" },
  currency: { key: "currency", label: "Currency" },
  container: { key: "container", label: "Container size (20/40)" },
  invoice_number: { key: "invoice_number", label: "Invoice number" },
};

export const FIELD_DEFS = {
  allocation: [F.vessel, F.lane, F.origin, F.destination, F.etd, F.week,
    { key: "quantity", label: "Allocated TEU", required: true }],
  booked: [F.vessel, F.lane, F.origin, F.destination, F.etd, F.week, F.reference,
    { key: "quantity", label: "Booked TEU", required: true }],
  forward: [F.vessel, F.lane, F.origin, F.destination, F.etd, F.week, F.reference,
    { key: "quantity", label: "Forecast TEU", required: true }],
  ratecard: [F.description, F.destination, F.origin, F.lane, F.container, F.vessel, F.unit, F.currency,
    { key: "rate", label: "Rate", required: true }],
  invoice: [F.description, F.invoice_number, F.reference, F.lane, F.origin, F.destination, F.container, F.vessel, F.unit, F.currency,
    { key: "quantity", label: "Quantity" }, { key: "rate", label: "Rate" }, { key: "amount", label: "Amount" }],
};

const HINTS = {
  vessel: ["vessel", "ship", "vsl", "carrier vessel"],
  lane: ["lane", "route", "trade", "service", "string", "corridor"],
  origin: ["origin", "pol", "load", "from", "departure port", "port of load"],
  destination: ["destination", "pod", "discharge", "to", "arrival port", "port of dis"],
  etd: ["etd", "sail", "sailing", "onboard", "departure", "etd date", "date"],
  week: ["week", "wk", "voyage week"],
  quantity: ["teu", "qty", "quantity", "volume", "containers", "ctr", "boxes", "units", "allocated", "booked", "forecast"],
  rate: ["rate", "price", "tariff", "unit rate", "base rate", "bafo", "sell", "buy", "nett", "net rate"],
  amount: ["amount", "total", "line total", "net", "value", "charge amount"],
  reference: ["ref", "booking", "bl", "b/l", "order no", "container no", "shipment"],
  description: ["supplier invoice description", "invoice description", "charge description", "description", "desc", "charge type", "charge", "item", "narration", "particular", "detail"],
  unit: ["unit", "uom", "per", "basis"],
  currency: ["currency", "curr", "ccy"],
  container: ["container size", "container type", "container", "ctr size", "cntr", "equipment", "cont type", "size", "box type"],
  invoice_number: ["invoice number", "invoice no", "invoice #", "inv no", "inv number", "invoice"],
};

// Suggest a mapping by matching column headers against keyword hints.
export function autoMap(columns, fields) {
  const mapping = {};
  const used = new Set();
  fields.forEach((f) => {
    const hints = HINTS[f.key] || [f.key];
    let best = null;
    for (const col of columns) {
      if (used.has(col)) continue;
      const lc = String(col).toLowerCase();
      if (hints.some((h) => lc === h)) { best = col; break; }
      if (!best && hints.some((h) => lc.includes(h))) best = col;
    }
    if (best) { mapping[f.key] = best; used.add(best); }
  });
  return mapping;
}

// Convert spreadsheet rows into normalized invoice line dicts using a mapping.
export function rowsToLines(rows, mapping) {
  return rows.map((r) => {
    const g = (f) => (mapping[f] ? r[mapping[f]] : "");
    return {
      description: g("description"), reference: g("reference"),
      invoice_number: g("invoice_number"),
      origin: g("origin"), destination: g("destination"), lane: g("lane"),
      vessel: g("vessel"), quantity: g("quantity"), unit: g("unit"), container: g("container"),
      rate: g("rate"), amount: g("amount"), currency: g("currency"),
    };
  });
}

export const RAG = {
  green: { bg: "#e7f6ef", fg: "#0f8a5f", dot: "#1baf7a", label: "On target" },
  amber: { bg: "#fdf3e0", fg: "#b7791f", dot: "#eda100", label: "Under-utilized" },
  red: { bg: "#fdeaea", fg: "#c5372f", dot: "#e5484d", label: "At risk" },
  grey: { bg: "#f1f5f9", fg: "#64748b", dot: "#94a3b8", label: "No allocation" },
};

// ---- MNA shipment workbook (standard multi-tab format) ----
const toISO = (v) => {
  if (v instanceof Date && !isNaN(v)) {
    const d = new Date(Date.UTC(v.getFullYear(), v.getMonth(), v.getDate()));
    return d.toISOString().slice(0, 10);
  }
  return v == null ? "" : String(v).trim();
};

function detectMnaCols(header) {
  const H = header.map((h) => String(h == null ? "" : h).replace(/\s+/g, " ").trim().toUpperCase());
  const find = (pred, from = 0) => { for (let i = from; i < H.length; i++) if (pred(H[i])) return i; return -1; };
  const vessel = find((h) => h === "VESSEL");
  return {
    order: find((h) => h === "ORDER"),
    pod: find((h) => h === "POD"),
    etdReq: find((h) => h.includes("ETD REQ")),
    proEtd: find((h) => h === "PROFORMA ETD"),
    vessel,
    voyage: find((h) => h === "VOYAGE", vessel + 1),
    booking: find((h) => h.includes("BOOKING")),
    status: find((h) => h === "STATUS"),
  };
}

// Parse the whole workbook: every monthly tab that has an ORDER column.
export function parseMnaWorkbook(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: true });
        const months = [];
        wb.SheetNames.forEach((name) => {
          const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false });
          if (!aoa.length) return;
          const c = detectMnaCols(aoa[0] || []);
          if (c.order < 0 || c.pod < 0) return; // not a shipment tab
          const rows = [];
          for (let i = 1; i < aoa.length; i++) {
            const r = aoa[i] || [];
            const get = (idx) => (idx >= 0 ? r[idx] : "");
            const order = String(get(c.order) || "").trim();
            if (!order) continue;
            rows.push({
              order,
              pod: String(get(c.pod) || "").trim(),
              etd_req: toISO(get(c.etdReq)),
              proforma_etd: toISO(get(c.proEtd)),
              vessel: String(get(c.vessel) || "").trim(),
              voyage: String(get(c.voyage) || "").trim(),
              booking: String(get(c.booking) || "").trim(),
              status: String(get(c.status) || "").trim(),
            });
          }
          if (rows.length) months.push({ name, rows });
        });
        resolve({ filename: file.name, months });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}
